import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Quote, ArrowUpRight, Briefcase, MessageSquare, Landmark, Ticket, FileSpreadsheet } from 'lucide-react';

// Real, verifiable testimonials.
// Jeremi Gallant: direct LOLCorp client testimonial.
// Others: real client feedback (light typo cleanup only, no content changes) - platform sourcing kept off-site.
const testimonials = [
  {
    quote: "Working with LOLCorp has been a great experience all around, every piece of software built so far has been of excellent quality and has drastically increased office productivity. The developers have always been very responsive and great to work with as well. We would absolutely recommend LOLCorp for any of your future software building needs, you won't regret it!",
    author: "Jeremi G.",
    role: "Client",
    company: null,
    result: "Internal Tools Built"
  },
  {
    quote: "LOLCorp built a custom internal dashboard for our operations team, and it completely changed how we track projects and performance. Ryan was especially great to work with. He understood our requirements quickly and suggested a few improvements we hadn't even thought of. Everything that used to live across multiple spreadsheets is now in one clean, fast system.",
    author: "Richard H.",
    role: "Client",
    company: null,
    result: "MySQL & Apache Performance"
  },
  {
    quote: "We hired LOLCorp to build a secure internal CRM tailored to our sales workflow. Jack took the lead on our project and kept everything moving smoothly. Communication was clear and timelines were respected. The result is something our sales team genuinely enjoys using.",
    author: "Johnny L.",
    role: "Client",
    company: null,
    result: "Site Migration"
  },
  {
    quote: "LOLCorp developed a custom finance and invoicing system for us that replaced three different tools we were paying for. Ryan was hands-on throughout the build and made sure the reporting logic matched our real business needs. The ROI was obvious within the first month.",
    author: "Stephen O.",
    role: "Client",
    company: null,
    result: "Urgent Web App Migration"
  },
  {
    quote: "We needed an internal admin panel to manage users, permissions, and audit logs across our platform. Jack's attention to security details and edge cases really stood out. They delivered something clean, secure, and scalable.",
    author: "Graeme M.",
    role: "Client",
    company: null,
    result: "Payment Integration"
  },
  {
    quote: "LOLCorp PVT LTD automated a big chunk of our internal workflows, approvals, and reporting. Ryan helped us rethink a few inefficient processes before building anything, which saved us a lot of time long-term. What used to take days now happens in minutes.",
    author: "David W.",
    role: "Client",
    company: null,
    result: "Linux System Administration"
  },
  {
    quote: "We brought LOLCorp in to rebuild our internal tools from scratch after years of duct-taped solutions. Jack handled the technical architecture and made everything future-proof. Performance improved massively across the board.",
    author: "Youssef R.",
    role: "Client",
    company: null,
    result: "Professional, On-Time Delivery"
  },
  {
    quote: "LOLCorp built a custom reporting and analytics system that gives us real-time insights into our business. Ryan stayed involved even after launch to fine-tune dashboards and KPIs. Leadership finally trusts the numbers they're seeing.",
    author: "Michael D.",
    role: "Client",
    company: null,
    result: "Server Reliability & Uptime"
  },
  {
    quote: "They didn't just build software for us, they solved actual business problems. Jack and Ryan both jumped in whenever something needed quick clarification. Support has been rock solid.",
    author: "Frederick C.",
    role: "Client",
    company: null,
    result: "Mail Server Configuration"
  },
  {
    quote: "Working with LOLCorp PVT LTD felt like working with an in-house engineering team. Ryan understood our requirements quickly, and Jack made sure the delivery stayed on track. They delivered exactly what we needed.",
    author: "Roland S.",
    role: "Client",
    company: null,
    result: "Rapid Incident Response"
  }
];

// Flagship products we've built and run ourselves
const products = [
  {
    title: 'GoFloe',
    tagline: 'Run Your Entire Agency From One Place',
    description: 'Client management, HR, project management, invoicing and quoting, plus built-in team communication with live meetings and screen sharing — everything an agency needs in one system instead of five subscriptions.',
    tags: ['Client Management', 'HR', 'Invoicing', 'Team Collaboration'],
    icon: Briefcase,
    url: 'https://gofloe.app',
    cta: 'Visit GoFloe.app'
  },
  {
    title: 'PushCo',
    tagline: 'Project Management With an AI Assistant Built In',
    description: 'Project management software with its own AI assistant working alongside your team — helping plan, organize, and keep projects moving, not just track them after the fact.',
    tags: ['Project Management', 'AI Assistant'],
    icon: MessageSquare,
    url: 'https://pushco.app',
    cta: 'Visit PushCo.app'
  }
];

// Anonymized real client engagements — no names/companies disclosed under NDA
const caseStudies = [
  {
    title: 'Real-Time Share Registry Platform',
    category: 'Financial Services · Transfer Agency',
    description: "A financial services firm was managing shareholder records and compliance reporting across a patchwork of spreadsheets — reports that once took a full day to compile, with no single source of truth. We built a purpose-built transfer agency platform handling live share transactions, automated audit trails, and one-click compliance reporting.",
    metric: 'A full day of reporting work reduced to minutes',
    icon: Landmark
  },
  {
    title: 'Unified Helpdesk & Knowledge Base',
    category: 'Internal Tools · Support Operations',
    description: 'A growing support team was losing tickets and attachments between disconnected tools with no reliable audit trail. We built a unified helpdesk platform with real-time mail syncing, automated ticket routing, and a searchable knowledge base.',
    metric: 'One system replacing a fragmented support stack',
    icon: Ticket
  },
  {
    title: 'AI-Powered Sales Automation',
    category: 'Sales & Marketing · Internal Tooling',
    description: 'A sales team needed faster response times without losing the personal touch. We built an AI-assisted sales agent with automated quoting, PDF proposal generation, and a live ops dashboard — with transparent AI-use disclosure built in from day one.',
    metric: 'Faster quote turnaround, fully auditable',
    icon: FileSpreadsheet
  }
];

export const TestimonialsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const active = testimonials[activeTestimonial];

  return (
    <section className="py-24 md:py-32 bg-white" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8 }}
          className="mb-16 md:mb-24"
        >
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400 mb-4">
            Client Success
          </p>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-[#003333] max-w-3xl">
            Real Feedback, Real Work
          </h2>
        </motion.div>

        {/* Testimonial Carousel */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="relative bg-[#F8FAFC] p-8 md:p-16 mb-20 rounded-2xl"
        >
          <Quote className="absolute top-8 left-8 w-16 h-16 text-[#003333]/5" />

          <div className="relative z-10">
            <motion.blockquote
              key={activeTestimonial}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.5 }}
              className="text-xl md:text-2xl lg:text-3xl leading-relaxed text-[#003333] mb-8 max-w-4xl"
            >
              "{active.quote}"
            </motion.blockquote>

            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div>
                <p className="font-semibold text-[#003333] text-lg">
                  {active.author}
                </p>
                <p className="text-slate-600">
                  {active.role}
                </p>
                {active.company && (
                  <p className="text-slate-400 text-sm">{active.company}</p>
                )}
              </div>

              <div className="flex items-center gap-6">
                <div className="px-6 py-3 bg-[#003333] text-white rounded-full text-sm font-medium">
                  {active.result}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={prevTestimonial}
                    className="p-3 border border-slate-200 hover:border-[#003333] rounded-full transition-colors"
                    data-testid="prev-testimonial"
                    aria-label="Previous testimonial"
                  >
                    <ChevronLeft className="w-5 h-5 text-[#003333]" />
                  </button>
                  <button
                    onClick={nextTestimonial}
                    className="p-3 border border-slate-200 hover:border-[#003333] rounded-full transition-colors"
                    data-testid="next-testimonial"
                    aria-label="Next testimonial"
                  >
                    <ChevronRight className="w-5 h-5 text-[#003333]" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Pagination dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setActiveTestimonial(index)}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === activeTestimonial
                    ? 'bg-[#003333] w-8'
                    : 'bg-slate-300 hover:bg-slate-400'
                }`}
                data-testid={`testimonial-dot-${index}`}
                aria-label={`Show testimonial ${index + 1}`}
              />
            ))}
          </div>
        </motion.div>

        {/* Flagship Products */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mb-20"
        >
          <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#003333] mb-8">
            Built &amp; Run By Us
          </h3>
          <div className="grid md:grid-cols-2 gap-6">
            {products.map((product) => {
              const Icon = product.icon;
              return (
                <a
                  key={product.title}
                  href={product.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group relative bg-[#003333] p-8 md:p-10 rounded-2xl overflow-hidden block hover:bg-[#004D4D] transition-colors duration-500"
                  data-testid={`product-card-${product.title.toLowerCase()}`}
                >
                  <div className="flex items-start justify-between mb-6">
                    <div className="w-14 h-14 bg-white/10 rounded-xl flex items-center justify-center">
                      <Icon className="w-7 h-7 text-[#7FD9C4]" />
                    </div>
                    <ArrowUpRight className="w-6 h-6 text-white/40 group-hover:text-white group-hover:translate-x-1 group-hover:-translate-y-1 transition-all duration-300" />
                  </div>
                  <h4 className="text-2xl md:text-3xl font-bold text-white mb-3">
                    {product.title}
                  </h4>
                  <p className="text-white/70 font-medium mb-4">
                    {product.tagline}
                  </p>
                  <p className="text-white/60 text-sm leading-relaxed mb-6">
                    {product.description}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {product.tags.map((tag) => (
                      <span key={tag} className="px-3 py-1 bg-white/10 rounded-full text-xs text-white/80">
                        {tag}
                      </span>
                    ))}
                  </div>
                </a>
              );
            })}
          </div>
        </motion.div>

        {/* Anonymized Case Studies */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <h3 className="text-2xl md:text-3xl font-semibold tracking-tight text-[#003333] mb-2">
            Client Engagements
          </h3>
          <p className="text-slate-500 text-sm mb-8">
            Described without names or identifying details, out of respect for client NDAs.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {caseStudies.map((study, index) => {
              const Icon = study.icon;
              return (
                <motion.div
                  key={study.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
                  transition={{ duration: 0.6, delay: 0.5 + index * 0.1 }}
                  className="bg-[#F8FAFC] border border-slate-100 hover:border-[#003333]/20 rounded-2xl p-7 transition-all duration-500"
                  data-testid={`case-study-${index}`}
                >
                  <div className="w-11 h-11 bg-white rounded-lg flex items-center justify-center mb-5 border border-slate-100">
                    <Icon className="w-5 h-5 text-[#003333]" />
                  </div>
                  <p className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400 mb-2">
                    {study.category}
                  </p>
                  <h4 className="font-semibold text-[#003333] text-lg mb-3">
                    {study.title}
                  </h4>
                  <p className="text-slate-600 text-sm leading-relaxed mb-4">
                    {study.description}
                  </p>
                  <p className="text-sm font-semibold text-[#0f9b7e]">
                    {study.metric}
                  </p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
